import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import { api, setSessionEndedHandler, type OtpRequestResult, type Permission, type User } from './api'

/** Why the last session ended, so the sign-in screen can say so. */
export type SessionEnd = 'expired' | 'signedOut' | null

interface AuthState {
  user: User | null
  loading: boolean
  /** Set when the session ended on its own (timeout, revoked, deactivated) or by signing out. */
  ended: SessionEnd
  /** Phone number, email or (older accounts) username, with the password. */
  signIn: (identifier: string, password: string) => Promise<void>
  requestOtp: (identifier: string) => Promise<OtpRequestResult>
  verifyOtp: (identifier: string, code: string) => Promise<void>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
  /** Whether the signed-in user holds a permission. The server checks again either way. */
  can: (permission: Permission) => boolean
}

const AuthContext = createContext<AuthState | null>(null)

/**
 * A note that this device had a session, so a reload after it timed out can
 * still say "your session ended" rather than showing a blank sign-in. Holds
 * no secret - the session itself is an httpOnly cookie.
 */
const HAD_SESSION = 'clinic.hadSession'
function markSession(on: boolean) {
  try {
    if (on) localStorage.setItem(HAD_SESSION, '1')
    else localStorage.removeItem(HAD_SESSION)
  } catch { /* storage blocked: the message is a nicety */ }
}
function hadSession(): boolean {
  try { return localStorage.getItem(HAD_SESSION) === '1' } catch { return false }
}

/** The phone app keeps the last queue on the device; a shared tablet must not keep it after sign-out. */
function clearDeviceCache() {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('clinic.m.cache.')) localStorage.removeItem(key)
    }
  } catch { /* storage blocked: nothing was cached either */ }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [ended, setEnded] = useState<SessionEnd>(null)
  const [idleMinutes, setIdleMinutes] = useState<number | null>(null)
  const userRef = useRef<User | null>(null)
  userRef.current = user

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<{ user: User; idleTimeoutMinutes?: number }>('/api/auth/me')
      setUser(res.user)
      markSession(true)
      if (res.idleTimeoutMinutes) setIdleMinutes(res.idleTimeoutMinutes)
    } catch {
      // A 401 here is the normal "not signed in yet" case, not an error. If
      // someone *was* signed in, their session has just ended.
      if (userRef.current || hadSession()) { clearDeviceCache(); setEnded('expired') }
      markSession(false)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Any request the server refuses as signed-out ends the session here too,
  // so a timed-out or deactivated user lands on sign-in rather than on a
  // screen of errors.
  useEffect(() => {
    setSessionEndedHandler(() => {
      if (!userRef.current) return
      clearDeviceCache()
      markSession(false)
      setEnded('expired')
      setUser(null)
    })
    return () => setSessionEndedHandler(null)
  }, [])

  // The server ends an idle session; this notices without waiting for the
  // next click. Any interaction resets the clock, and when it runs out the
  // session is checked - if the server still has it (another tab kept it
  // alive), nothing happens.
  useEffect(() => {
    if (!user || !idleMinutes) return
    let timer: ReturnType<typeof setTimeout>
    const arm = () => {
      clearTimeout(timer)
      timer = setTimeout(() => { void refresh() }, idleMinutes * 60_000 + 5_000)
    }
    const events = ['pointerdown', 'keydown', 'visibilitychange'] as const
    events.forEach((e) => window.addEventListener(e, arm, { passive: true }))
    arm()
    return () => {
      clearTimeout(timer)
      events.forEach((e) => window.removeEventListener(e, arm))
    }
  }, [user, idleMinutes, refresh])

  const landed = useCallback((res: { user: User; idleTimeoutMinutes?: number }) => {
    setEnded(null)
    setUser(res.user)
    markSession(true)
    if (res.idleTimeoutMinutes) setIdleMinutes(res.idleTimeoutMinutes)
  }, [])

  const signIn = useCallback(async (identifier: string, password: string) => {
    landed(await api.post<{ user: User; idleTimeoutMinutes?: number }>('/api/auth/login', { identifier, password }))
  }, [landed])

  const requestOtp = useCallback(
    (identifier: string) => api.post<OtpRequestResult>('/api/auth/otp/request', { identifier }),
    [],
  )

  const verifyOtp = useCallback(async (identifier: string, code: string) => {
    landed(await api.post<{ user: User; idleTimeoutMinutes?: number }>('/api/auth/otp/verify', { identifier, code }))
  }, [landed])

  const signOut = useCallback(async () => {
    try {
      await api.post('/api/auth/logout')
    } finally {
      // Signed out on this device even if the server could not be reached.
      clearDeviceCache()
      setEnded('signedOut')
      markSession(false)
      setUser(null)
    }
  }, [])

  const can = useCallback((p: Permission) => !!user?.permissions?.includes(p), [user])

  const value = useMemo(
    () => ({ user, loading, ended, signIn, requestOtp, verifyOtp, signOut, refresh, can }),
    [user, loading, ended, signIn, requestOtp, verifyOtp, signOut, refresh, can],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
